"use client";

import { cn } from "@jesus-central-church/shared";
import { motion, useInView, useScroll, useTransform } from "motion/react";
import { useRef } from "react";
import { Image } from "./image";

interface AnimatedImageProperties {
  src: string;
  alt: string;
  side?: "left" | "right";
  delay?: number;
  className?: string;
  imageClassName?: string;
  quality?: number;
  sizes?: string;
  priority?: boolean;
  loading?: "lazy" | "eager" | undefined;
  useScrollAnimation?: boolean;
}

export function AnimatedImage({
  src,
  alt,
  side = "right",
  delay = 0,
  className,
  imageClassName,
  priority,
  quality,
  loading,
  sizes = "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
  useScrollAnimation = true,
}: AnimatedImageProperties) {
  const reference = useRef(null);
  const isInView = useInView(reference, { once: true, margin: "-10%" });

  const { scrollYProgress } = useScroll({
    target: reference,
    offset: ["start end", "end start"],
  });

  // Only use scroll transforms if explicitly enabled
  const imageY = useScrollAnimation
    ? useTransform(scrollYProgress, [0, 0.5, 1], [40, 0, -40])
    : undefined;
  const imageRotate = useScrollAnimation
    ? useTransform(
      scrollYProgress,
      [0, 0.5, 1],
      side === "left" ? [-4, 0, 4] : [4, 0, -4],
    )
    : undefined;
  const imageOpacity = useScrollAnimation
    ? useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.3, 1, 1, 0.3])
    : undefined;

  return (
    <motion.div
      ref={reference}
      style={{
        ...(useScrollAnimation && {
          y: imageY,
          rotate: imageRotate,
          opacity: imageOpacity,
        }),
        willChange: useScrollAnimation ? "transform, opacity" : "transform",
      }}
      initial={{
        opacity: 0,
        scale: 0.95,
        rotateY: side === "left" ? -8 : 8,
      }}
      animate={
        isInView
          ? {
            opacity: useScrollAnimation ? undefined : 1,
            scale: 1,
            rotateY: 0,
          }
          : {
            opacity: 0,
            scale: 0.95,
            rotateY: side === "left" ? -8 : 8,
          }
      }
      transition={{
        duration: 0.5,
        delay,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{
        scale: 1.03,
        transition: {
          type: "spring",
          stiffness: 400,
          damping: 25,
        },
      }}
      className={cn(
        "relative h-[300px] overflow-hidden rounded-2xl shadow-2xl md:h-[400px] md:rounded-3xl lg:h-[500px]",
        className,
      )}
    >
      <div className="absolute inset-0">
        <Image
          src={src}
          alt={alt}
          fill
          objectFit="cover"
          className={cn(imageClassName)}
          quality={quality}
          sizes={sizes}
          priority={priority}
          loading={loading}
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
    </motion.div>
  );
}
